export type CarePassProfile = "local" | "development" | "production";

export interface RuntimeEnvironment {
  readonly VITE_CAREPASS_PROFILE?: string;
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_ASYNC_POLL_INITIAL_DELAY_MS?: string;
  readonly VITE_ASYNC_POLL_MAX_DELAY_MS?: string;
  readonly VITE_ASYNC_POLL_TIMEOUT_MS?: string;
}

export interface LocationLike {
  readonly hostname: string;
  readonly origin: string;
}

export interface PollingRuntimeConfig {
  readonly initialDelayMs: number;
  readonly maxDelayMs: number;
  readonly timeoutMs: number;
}

export interface CarePassRuntimeConfig {
  readonly profile: CarePassProfile;
  readonly apiBaseUrl: string;
  readonly appOrigin: string;
  readonly polling: PollingRuntimeConfig;
}

const DEFAULT_API_BASE_URL: Record<CarePassProfile, string> = {
  local: "/api",
  development: "https://api-dev.carepass.co.kr",
  production: "https://carepass.co.kr",
};

const DEFAULT_POLLING: PollingRuntimeConfig = {
  initialDelayMs: 1_000,
  maxDelayMs: 10_000,
  timeoutMs: 120_000,
};

function normalizeProfile(value: string | undefined, hostname: string): CarePassProfile {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "local") return "local";
  if (normalized === "development" || normalized === "dev") return "development";
  if (normalized === "production" || normalized === "prod") return "production";
  if (normalized) throw new Error(`Unsupported CarePass profile: ${value}`);

  if (hostname === "localhost" || hostname === "127.0.0.1") return "local";
  if (
    hostname === "dev.carepass.co.kr" ||
    hostname === "admin-dev.carepass.co.kr" ||
    hostname === "api-dev.carepass.co.kr" ||
    hostname.endsWith(".dev.carepass.co.kr")
  ) {
    return "development";
  }
  return "production";
}

function normalizeBaseUrl(value: string, profile: CarePassProfile, origin: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("CarePass API base URL must not be empty.");

  const resolved = new URL(trimmed, origin);
  if (resolved.username || resolved.password || resolved.search || resolved.hash) {
    throw new Error("CarePass API base URL must not contain credentials, query, or fragment values.");
  }
  if (profile !== "local" && resolved.protocol !== "https:") {
    throw new Error("Development and production API base URLs must use HTTPS.");
  }

  const normalizedPath = resolved.pathname.replace(/\/$/, "");
  if (trimmed.startsWith("/")) return normalizedPath || "/";
  return `${resolved.origin}${normalizedPath}`;
}

function positiveInteger(value: string | undefined, fallback: number, name: string): number {
  if (value === undefined || value.trim() === "") return fallback;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new Error(`${name} must be a positive integer.`);
  }
  return parsed;
}

export function createRuntimeConfig(
  environment: RuntimeEnvironment,
  location: LocationLike,
): CarePassRuntimeConfig {
  const profile = normalizeProfile(environment.VITE_CAREPASS_PROFILE, location.hostname);
  const apiBaseUrl = normalizeBaseUrl(
    environment.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL[profile],
    profile,
    location.origin,
  );
  const initialDelayMs = positiveInteger(
    environment.VITE_ASYNC_POLL_INITIAL_DELAY_MS,
    DEFAULT_POLLING.initialDelayMs,
    "VITE_ASYNC_POLL_INITIAL_DELAY_MS",
  );
  const maxDelayMs = positiveInteger(
    environment.VITE_ASYNC_POLL_MAX_DELAY_MS,
    DEFAULT_POLLING.maxDelayMs,
    "VITE_ASYNC_POLL_MAX_DELAY_MS",
  );
  const timeoutMs = positiveInteger(
    environment.VITE_ASYNC_POLL_TIMEOUT_MS,
    DEFAULT_POLLING.timeoutMs,
    "VITE_ASYNC_POLL_TIMEOUT_MS",
  );

  if (maxDelayMs < initialDelayMs) {
    throw new Error("The maximum polling delay must be greater than or equal to the initial delay.");
  }
  if (timeoutMs < initialDelayMs) {
    throw new Error("The polling timeout must be greater than or equal to the initial delay.");
  }

  return {
    profile,
    apiBaseUrl,
    appOrigin: location.origin,
    polling: { initialDelayMs, maxDelayMs, timeoutMs },
  };
}
